// IE 2026 10 29 / ODOO-5800 (заказ Игоря: длинная задача Shift+F3)
//
// Замер 29.10 (CPU-профиль shiftf3-longtask, разбор handoff-files/shiftf3-longtask-2026-10-29/SECOND-REFRESH.txt): после Shift+F3 грид
// Stock Move (4591 × 29) делает явный flex.refresh() в flexRefresh_afterTimeout, а через ~100 мс — ВТОРОЙ полный FlexGrid.refresh
// (165–250 мс), запущенный таймером wijmo Control.invalidate(). Его взводит AttrGrid.autoSizeRows: порции по 500 строк идут после
// await store.sleep(0) внутри flex.deferUpdate(fn), а Control.endUpdate(shouldInvalidate = true) в wijmo 5.20261.52 БЕЗУСЛОВНО зовёт
// invalidate() (таймер 10 мс → refresh(fullUpdate)), даже если fn не изменила ни одной высоты. На плоском гриде (без секций и
// _DLRow.height) высоты не меняются никогда — refresh пустой.
//
// При флаге autoSizeRows сначала строит план высот без записи (autoSizeRows_plan — та же семантика, что у старого цикла: скрытые
// строки пропускаются и «предыдущей» не считаются; начало секции = уровень меньше предыдущей видимой; rowH из _DLRow.height /
// _cells._row.height; начало секции → (rowH || defaultSize) + breakTop, при breakTop 0 → rowH || null; иначе rowH; нет высоты →
// сброс к null), и если порция ничего не меняет — в deferUpdate не входит вовсе (invalidate не взводится). Изменённые строки
// применяются внутри одного deferUpdate на порцию, как раньше. Для ветки autoRowHeights — beginUpdate / endUpdate(изменено > 0).
//
// Выключатель fe.ux.gridAutoSizeRowsLazy (§8, по умолчанию ВКЛ, без пересборки): window.feSettings.set('fe.ux.gridAutoSizeRowsLazy', false)
// или ?fe.ux.gridAutoSizeRowsLazy=false — старый цикл с deferUpdate на каждую порцию. Счётчики (тихо, без console):
// window.gridAutoSizeRows.get() / .set(false) / .reset() / .stats() / .clear().

export const FLAG = 'fe.ux.gridAutoSizeRowsLazy'
export const GASR_DEFAULTS = { [FLAG]: true }

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())

const stats = {
    calls: 0,          // вызовов autoSizeRows при флаге
    rowsSeen: 0,       // строк просмотрено планировщиком (включая скрытые)
    rowsChanged: 0,    // строк, у которых высота реально меняется
    batchesApplied: 0, // порций, применённых в deferUpdate (были изменения)
    batchesSkipped: 0, // порций без изменений — deferUpdate / invalidate не было
    msPlan: 0,         // мс в планировщике (performance.now)
    last: null,        // последний вызов: { rows, seen, changed, applied, skipped, ms }
}

export function gridAutoSizeRows_on(store) {
    return !store || typeof store.setting !== 'function' || store.setting(FLAG, GASR_DEFAULTS[FLAG]) !== false
}

// высота строки из настройки строки лэйаута (IE 2026 09 03 / ODOO-336283)
export function autoSizeRows_rowH(dataItem) {
    return dataItem?._DLRow?.height || dataItem?._cells?._row?.height || 0
}

// План высот порции [start, end) без записи. rows — массив (или wijmo RowCollection) объектов { visibleIndex, level, height, dataItem }.
// Возвращает [{ i, row, height }] только там, где запись row.height что-то изменит (сравнение как в сеттере wijmo RowCol.size:
// `e != this._sz`; сброс — только если высота задана). «Предыдущая» строка ищется по всему массиву (через границу порции), как раньше.
export function autoSizeRows_plan(rows, { defaultSize, breakTop = 0, start = 0, end = rows.length } = {}) {
    const changes = []
    for (let i = start; i < end; i++) {
        const row = rows[i]
        if (row.visibleIndex === -1) continue
        let iPrev = i - 1
        while (iPrev >= 0 && rows[iPrev].visibleIndex === -1) iPrev--
        const prevRow = iPrev >= 0 ? rows[iPrev] : null
        const sectionStart = !!(prevRow && row.level < prevRow.level)
        const rowH = autoSizeRows_rowH(row.dataItem)

        let newHeight = null
        if (sectionStart) {
            newHeight = breakTop ? (rowH || defaultSize) + breakTop : (rowH || null)
        } else if (rowH) {
            newHeight = rowH
        }

        if (newHeight !== null) {
            if (row.height != newHeight) changes.push({ i, row, height: newHeight })
        } else if (row.height != null) {
            changes.push({ i, row, height: null })
        }
    }
    return changes
}

// учёт вызова: begin → (batch …) → end
export function autoSizeRows_begin(nRows) {
    stats.calls++
    return { rows: nRows, seen: 0, changed: 0, applied: 0, skipped: 0, ms: 0 }
}

// спланировать порцию с учётом времени и счётчиков
export function autoSizeRows_batch(acc, rows, opts) {
    const t0 = now()
    const changes = autoSizeRows_plan(rows, opts)
    const ms = now() - t0
    const seen = Math.max(0, (opts?.end ?? rows.length) - (opts?.start ?? 0))
    acc.seen += seen
    acc.changed += changes.length
    acc.ms += ms
    if (changes.length) { acc.applied++; stats.batchesApplied++ } else { acc.skipped++; stats.batchesSkipped++ }
    stats.rowsSeen += seen
    stats.rowsChanged += changes.length
    stats.msPlan += ms
    return changes
}

export function autoSizeRows_end(acc) {
    stats.last = { ...acc, ms: Math.round(acc.ms * 10) / 10 }
    return stats.last
}

export function gridAutoSizeRows_stats() {
    return { ...stats, msPlan: Math.round(stats.msPlan * 10) / 10, last: stats.last ? { ...stats.last } : null }
}

export function gridAutoSizeRows_clear() {
    for (const k of Object.keys(stats)) stats[k] = typeof stats[k] === 'number' ? 0 : null
    return gridAutoSizeRows_stats()
}

export function gridAutoSizeRows_console(store) {
    if (typeof window === 'undefined') return
    window.gridAutoSizeRows = {
        flag: FLAG,
        get: () => ({ on: gridAutoSizeRows_on(store), stats: gridAutoSizeRows_stats() }),
        set(value) { window.feSettings?.set?.(FLAG, value); return this.get() },
        reset() { window.feSettings?.unset?.(FLAG); return this.get() },
        stats: () => gridAutoSizeRows_stats(),
        clear: () => gridAutoSizeRows_clear(),
        help: () => 'gridAutoSizeRows.set(false) — autoSizeRows входит в deferUpdate на каждой порции (как до 29.10, лишний полный refresh wijmo); .reset() — снять override; .stats() / .clear() — счётчики',
    }
}
